import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import APFormGroup from 'components/FormFields/APFormGroup';
import InputField from 'components/FormFields/InputField';
import FormSelect from 'components/FormFields/FormSelect';
import { imagePickSize } from 'utils/general';
import { fetchImageStatus, fetchImage } from 'apollo/images.query';
import { uploadImage, imageProcess, getImageOriginalInfo } from 'api/image';
import { TIME_ZONES, TIME_ZONES_ORDER } from 'utils/constants';
import PhotoUploader from '../../AccountSetUp/PhotoCropper/ImageUploader';
import PhotoCropper from '../../AccountSetUp/PhotoCropper/ImageCropper';

const imageSizeLimit = Number(process.env.REACT_APP_IMAGE_SIZE_LIMIT);

const Profile = ({ userInfo, updateUserInfo, userInfoErrors, loading, setLoading }) => {
  const [firstName, setFirstName] = useState(null);
  const [lastName, setLastName] = useState(null);
  const [userName, setUserName] = useState(null);
  const [email, setEmail] = useState(null);
  const [createdAt, setCreatedAt] = useState(null);
  const [fieldChanged, setFieldChanged] = useState(false);
  const [timeZone, setTimeZone] = useState(null);

  const [originalImg, setOriginalImage] = useState(null);
  const [originalImgKey, setOriginalImageKey] = useState(null);
  const [imageCropPosition, setImageCropPosition] = useState(null);
  const [uploadingImg, setUploadingImage] = useState(null);
  const [profileImageUrl, setProfileImage] = useState(null);
  const [imageName, setImageName] = useState('');

  const [openImageCropper, setImageCropperOpen] = useState(false);
  const [isImgInvalid, setImgInvalid] = useState(false);
  const [errorTypes, setErrorTypes] = useState(null);

  const [imgLoadStatus, setImageLoadStatus] = useState('');
  const [imgTextStatus, setImageTextStatus] = useState('');

  const [isFormLoaded, setFormLoaded] = useState(false);

  useEffect(() => {
    if (userInfo.id) {
      let profileImg = null;
      if (userInfo?.profileImage?.resolutions?.full?.path) {
        profileImg = `//${process.env.REACT_APP_AWS_CLOUDFRONT}/${imagePickSize(
          userInfo.profileImage,
          'full',
        )}`;
      }

      let origImgKey = null;
      if (userInfo?.profileImage?.resolutions?.original?.original_key) {
        origImgKey = userInfo?.profileImage?.resolutions?.original?.original_key;
      } else if (userInfo?.profileImage?.resolutions?.full?.path) {
        const regex = /\/([^/]+)\.(jpg|png)$/;
        const match = userInfo?.profileImage?.resolutions?.full?.path.match(regex);
        origImgKey = match ? match[1] : null;
      }

      setFirstName(userInfo.firstName || '');
      setLastName(userInfo.lastName || '');
      setUserName(userInfo.username || '');
      setProfileImage(profileImg);
      setEmail(userInfo.email || '');
      setCreatedAt(userInfo.createdAt ? dayjs(new Date(userInfo.createdAt)).format('MMMM YYYY') : null);
      setTimeZone(userInfo.timezone || '');

      setOriginalImageKey(origImgKey);
      getOriginalImage(origImgKey, true);
      setImageCropPosition(userInfo.profileImage?.cropPosition);
      setFormLoaded(true);
    }
  }, [userInfo]);

  useEffect(() => {

    if (isImgInvalid) {
      const { acceptType, maxFileSize } = errorTypes || {};
      if (acceptType) {
        setImageTextStatus('Please upload your image in one of the following formats: JPEG or PNG.');
      } else if (maxFileSize) {
        setImageTextStatus(`Sorry, the image exceeds the maximum allowed size (${imageSizeLimit} MB).`);
      } else {
        setImageTextStatus('Something went wrong with uploading your image. Please try again.');
      }
    } else {
      switch (imgLoadStatus) {
        case 'loading':
        case 'upload-success':
          setImageTextStatus('Updating Profile Image...');
          break;
        case 'success':
          setImageTextStatus(' Updated Profile Image Successfully');
          break;
        case 'failed':
          setImageTextStatus('Updating Profile Image Failed');
          break;
        default:
          setImageTextStatus('');
          break;
      }
    }
  }, [imgLoadStatus, isImgInvalid, errorTypes]);

  const getError = (field) =>  userInfoErrors.find(item => item.field === field)?.error;
  const usernameError = getError('username');
  const emailError = getError('email');

  const onDropFile = (acceptedPhoto, filename) => {
    setImageCropperOpen(true);
    setImageName(filename);
    setUploadingImage(acceptedPhoto);
    setOriginalImageKey(null);
    setImageCropPosition(null);
  };

  let timer;
  const getImageStatus = async (key) => {
    try {
      const dataStatus = await fetchImageStatus(key);
      if (dataStatus.image?.status === 'processed') {
        clearInterval(timer);
        updateUserInfo({ profile_image_key: key });
        setImageLoadStatus('upload-success');
        setLoading(false);
      }
      else if (dataStatus.image?.status === 'error') {
        clearInterval(timer);
        setImageLoadStatus('failed');
        setLoading(false);
      }
    } catch (err) {
      clearInterval(timer);
      setImageLoadStatus('failed');
      setLoading(false);
    }
  };

  const getOriginalImage = async (key, getOriginal=false) => {
    if (key) {
      const imageB64 = true;
      try {
        const data = await fetchImage(key, imageB64, getOriginal);
        if (data.image?.status === 'processed') {
          const imageBytesB64 = data.image?.imageBytesB64;
          setOriginalImage(imageBytesB64 ? `data:image/png;base64,${imageBytesB64}` : null);
          if (imgLoadStatus === 'upload-success') {
            setImageLoadStatus('success');
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      }
    }
  };

  const processImage = async (binaryOrig, hashcodeOrig, binary, hashcode, origImgHasChanged, crop) => {
    const infoResponse = await getImageOriginalInfo({
      path: 'profile_images_original',
      binaryOrig,
      hashcodeOrig,
      imageName,
      binary,
      hashcode,
      origImgHasChanged,
      crop,
    });

    const { status, data } = infoResponse;

    if (status === 200) {
      const urlOrig = data.form_orig.action;
      const fieldsOrig = data.form_orig.fields;;
      const uploadResponseOrig = await uploadImage(urlOrig, fieldsOrig, binaryOrig, crop);

      const url = data.form.action;
      const uploadResponse = await uploadImage(url, data.form.fields, binary, crop);

      if(uploadResponseOrig.status === 204 && uploadResponse.status === 204) {
        const response = await imageProcess({
          path: 'profile_image',
          key: uploadResponse.key,
          method: 'post',
        });

        if (response.status === 200) {
          return {
            key: uploadResponse.key,
            keyOrig: uploadResponseOrig.key,
          };
        }
      }
    }
  };

  const onSaveProfileImage = async (
    fullImg,
    croppedImg,
    binary,
    hashcode,
    origImgBinary,
    origImgHashcode,
    origImgHasChanged = false,
    crop,
  ) => {
    setOriginalImage(fullImg);
    setUploadingImage(null);
    setProfileImage(croppedImg);
    setImageLoadStatus('loading');
    setLoading(true);

    const { key, keyOrig } = await processImage(origImgBinary,
      origImgHashcode,
      binary,
      hashcode,
      origImgHasChanged,
      crop);

    setOriginalImageKey(keyOrig);

    if(key) {
      timer = setInterval(
        () => getImageStatus(key),
        1000,
      );
    } else {
      setImageLoadStatus('failed');
    }
  };

  const onRemoveProfileImage = () => {
    setProfileImage(null);
    setOriginalImage(null);
    setOriginalImageKey(null);
    setImageCropPosition(null);
    setImageLoadStatus('');
    updateUserInfo({ profile_image_key: null });
  };

  const updateUserInfoField = (payload) => {
    updateUserInfo(payload);
  };

  if (!isFormLoaded) {
    return null;
  }

  return (
    <div>
      <APFormGroup
        title="Profile Photo"
        direction="vertical"
      >
        <PhotoCropper
          src={uploadingImg || originalImg}
          open={openImageCropper}
          onClose={() => {
            setImageCropperOpen(false);
          }}
          onSave={onSaveProfileImage}
          cropperConfig={imageCropPosition}
          sizeLimit={imageSizeLimit}
        />
        <PhotoUploader
          src={profileImageUrl}
          handleOnDrop={onDropFile}
          handleDelete={onRemoveProfileImage}
          setImageError={(value) => setImgInvalid(value)}
          setErrorTypes={(value) => setErrorTypes(value)}
          isLoading={!isImgInvalid && (imgLoadStatus === 'loading' || imgLoadStatus === 'upload-success' || loading)}
          sizeLimit={imageSizeLimit}
        />
        <div className="image-footer">
          {profileImageUrl && originalImgKey && (
            <div className="mt-2 pb-1">
              <span
                className="adjust-button"
                onClick={() => {
                  setImageCropperOpen(true);
                }}
              >
                Adjust Image
              </span>
            </div>
          )}
          <div className="image-error">{imgTextStatus}</div>
        </div>
      </APFormGroup>

      <APFormGroup
        title="Basic Information"
        direction="horizontal"
        className="account-group"
      >
        <InputField
          name="first_name"
          label="First name"
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
          }}
          showError={firstName === ''}
          onBlur={() => {
            if (firstName !== '' && userInfo.firstName !== firstName) {
              updateUserInfoField({ firstname: firstName });
            }
          }}
        />
        <InputField
          name="last_name"
          label="Last name"
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
          }}
          showError={lastName === ''}
          onBlur={() => {
            if (lastName !== '' && userInfo.lastName !== lastName) {
              updateUserInfoField({ lastname: lastName });
            }
          }}
        />
      </APFormGroup>

      <APFormGroup className="account-group">
        <InputField
          name="username"
          label="Username"
          value={userName}
          onChange={(e) => {
            setUserName(e.target.value);
            setFieldChanged(true);
          }}
          onBlur={() => {
            if (userName !== '' && fieldChanged) {
              updateUserInfoField({ username: userName });
            }
            setFieldChanged(false);
          }}
          showError={userName === '' || usernameError}
          error={usernameError}
        />
        <InputField
          name="created-at"
          label="Member since"
          defaultValue={createdAt}
          disabled
        />
      </APFormGroup>
      <APFormGroup className="account-group">
        <InputField
          name="email"
          label="Email address"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setFieldChanged(true);
          }}
          onBlur={() => {
            if (email !== '' && fieldChanged) {
              updateUserInfoField({ email });
            }
            setFieldChanged(false);
          }}
          showError={email === '' || emailError}
          error={emailError}
        />
        <FormSelect
          name="Time Zone"
          value={timeZone}
          onChange={(e) => {
            setTimeZone(e.target.value);
            updateUserInfoField({ timezone: e.target.value });
          }}
          showSearch
        >
          {TIME_ZONES_ORDER.map((option, index) => (
            <optgroup
              label={option.value}
              key={`option_${index}`}
            >
              {TIME_ZONES.filter((tz) => tz.group === option.value).map(
                (optionTZ, indexTZ) => (
                  <option
                    value={optionTZ.value}
                    key={`optionTZ_${option.value}_${indexTZ}`}
                  >
                    {optionTZ.label}
                  </option>
                ))}
            </optgroup>
          ))}
        </FormSelect>
      </APFormGroup>
    </div>
  );
};

export default Profile;
